import { useEffect, useRef, useState } from 'react';

/**
 * Stateful hook pro přehrávání historické jízdy (timeline / play). Načte
 * body trasy z `/api/flotila/gps/jizdy/{id}` a poskytne:
 *   - body: pole bodů trasy { lat, lng, rychlost_kmh, smer_stupne, zaznameno_at }
 *   - progress: 0..1 (0 = start, 1 = konec)
 *   - playing: bool
 *   - speed: 1 / 4 / 16 (násobek reálného času)
 *   - currentPoint: index a interpolovaná pozice odpovídající `progress`
 *   - controls: play / pause / stop / seek(progress) / setSpeed
 *
 * Auto-play je rAF loop — progress narůstá podle wall-clock × speed
 * vzhledem k délce jízdy (rozdíl prvního a posledního zaznameno_at).
 *
 * Lift state do parent komponenty (Flotila.jsx) a propsat dolů — Inspector
 * i MapVozidla musí číst stejné `progress` a `body`.
 */
export default function useJizdaPlayback(jizdaId) {
    const [body, setBody] = useState([]);
    const [progress, setProgress] = useState(1); // default = konec, vidíme celou trasu
    const [playing, setPlaying] = useState(false);
    const [speed, setSpeed] = useState(4); // default 4× — pizza jízda 20 min přehraje za 5 min
    const lastTsRef = useRef(null);
    const rafRef = useRef(null);

    // Reset při změně jízdy
    useEffect(() => {
        if (!jizdaId) {
            setBody([]);
            setProgress(1);
            setPlaying(false);
            return;
        }
        fetch(`/api/flotila/gps/jizdy/${jizdaId}`)
            .then((r) => r.json())
            .then((d) => {
                const trasa = Array.isArray(d?.trasa) ? d.trasa : [];
                setBody(trasa);
                setProgress(1);
                setPlaying(false);
            })
            .catch(() => setBody([]));
    }, [jizdaId]);

    // Auto-play rAF loop
    useEffect(() => {
        if (!playing || body.length < 2) return;

        const startAt = body[0]?.zaznameno_at;
        const endAt = body[body.length - 1]?.zaznameno_at;
        if (!startAt || !endAt) return;
        const totalSec = Math.max(1, (new Date(endAt) - new Date(startAt)) / 1000);

        lastTsRef.current = performance.now();
        const tick = (now) => {
            const dtSec = (now - lastTsRef.current) / 1000;
            lastTsRef.current = now;
            const dProgress = (dtSec * speed) / totalSec;

            setProgress((p) => {
                const next = p + dProgress;
                if (next >= 1) {
                    setPlaying(false);
                    return 1;
                }
                return next;
            });
            rafRef.current = requestAnimationFrame(tick);
        };
        rafRef.current = requestAnimationFrame(tick);

        return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
    }, [playing, body, speed]);

    // Aktuální bod — index + interpolovaná pozice mezi sousedními body podle progress.
    const currentPoint = (() => {
        if (body.length === 0) return null;
        if (body.length === 1) return { ...body[0], index: 0 };
        const exactIdx = progress * (body.length - 1);
        const i0 = Math.floor(exactIdx);
        const i1 = Math.min(body.length - 1, i0 + 1);
        const t = exactIdx - i0;
        const b0 = body[i0];
        const b1 = body[i1];
        if (!b0 || !b1) return null;
        // Interpoluj i čas, aby downstream komponenty (counter "u zákazníka",
        // sledování doručení podle playback času) tikaly plynule, ne jen na
        // přechodech mezi GPS body (které jsou typicky 15s daleko).
        const t0 = b0.zaznameno_at ? new Date(b0.zaznameno_at).getTime() : 0;
        const t1 = b1.zaznameno_at ? new Date(b1.zaznameno_at).getTime() : t0;
        const interpolatedTime = new Date(t0 + (t1 - t0) * t).toISOString();
        return {
            lat: Number(b0.lat) + (Number(b1.lat) - Number(b0.lat)) * t,
            lng: Number(b0.lng) + (Number(b1.lng) - Number(b0.lng)) * t,
            rychlost_kmh: Number(b0.rychlost_kmh) + (Number(b1.rychlost_kmh) - Number(b0.rychlost_kmh)) * t,
            zaznameno_at: interpolatedTime,
            index: i0,
        };
    })();

    return {
        body,
        progress,
        playing,
        speed,
        currentPoint,
        setSpeed,
        seek: (p) => setProgress(Math.max(0, Math.min(1, p))),
        play: () => {
            // Pokud je na konci, restart od začátku
            setProgress((p) => (p >= 0.999 ? 0 : p));
            setPlaying(true);
        },
        pause: () => setPlaying(false),
        stop: () => { setPlaying(false); setProgress(1); },
    };
}
